import { useEffect, useState } from "react";

export default function useCounselingGestures({
  targetGroup,
  setTargetGroup,
  isMobile,
  isCreatingLog,
  viewingLog,
  viewingStudent,
  minSwipeDistance = 80,
  maxVerticalDistance = 80,
}) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const onListSwipeEnd = () => {
    if (touchStart == null || touchEnd == null || touchStartY == null || touchEndY == null) return;
    const distance = touchStart - touchEnd;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    const isHorizontalIntent = Math.abs(distance) > minSwipeDistance && verticalDistance < maxVerticalDistance;
    if (!isHorizontalIntent) return;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && targetGroup === "students" && setTargetGroup) {
      setTargetGroup("teachers");
    } else if (isRightSwipe && targetGroup === "teachers" && setTargetGroup) {
      setTargetGroup("students");
    }
  };

  const onModalSwipeEnd = (closeHandler) => {
    if (touchStart == null || touchEnd == null || touchStartY == null || touchEndY == null) return;
    const distance = touchStart - touchEnd;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    const isHorizontalIntent = Math.abs(distance) > minSwipeDistance && verticalDistance < maxVerticalDistance;
    if (!isHorizontalIntent) return;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isRightSwipe) {
      closeHandler();
    }
  };

  const onVerticalSwipeEnd = (closeHandler) => {
    if (!touchStartY || !touchEndY) return;
    const distance = touchEndY - touchStartY;
    const isDownSwipe = distance > minSwipeDistance;
    if (isDownSwipe) {
      closeHandler();
    }
  };

  const [creationModalPos, setCreationModalPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input") || e.target.closest("textarea")) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - creationModalPos.x,
      y: e.clientY - creationModalPos.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setCreationModalPos({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isCreatingLog) {
      setCreationModalPos({ x: 0, y: 0 });
    }
  }, [isCreatingLog]);

  const [logDetailModalPos, setLogDetailModalPos] = useState({ x: 0, y: 0 });
  const [isLogDetailDragging, setIsLogDetailDragging] = useState(false);
  const [logDetailDragStart, setLogDetailDragStart] = useState({ x: 0, y: 0 });

  const handleLogDetailMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input") || e.target.closest("textarea")) return;
    setIsLogDetailDragging(true);
    setLogDetailDragStart({
      x: e.clientX - logDetailModalPos.x,
      y: e.clientY - logDetailModalPos.y,
    });
  };

  useEffect(() => {
    const handleLogDetailMouseMove = (e) => {
      if (!isLogDetailDragging) return;
      setLogDetailModalPos({
        x: e.clientX - logDetailDragStart.x,
        y: e.clientY - logDetailDragStart.y,
      });
    };

    const handleLogDetailMouseUp = () => {
      setIsLogDetailDragging(false);
    };

    if (isLogDetailDragging) {
      window.addEventListener("mousemove", handleLogDetailMouseMove);
      window.addEventListener("mouseup", handleLogDetailMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleLogDetailMouseMove);
      window.removeEventListener("mouseup", handleLogDetailMouseUp);
    };
  }, [isLogDetailDragging, logDetailDragStart]);

  useEffect(() => {
    if (viewingLog) {
      setLogDetailModalPos({ x: 0, y: 0 });
    }
  }, [viewingLog]);

  const [detailModalPos, setDetailModalPos] = useState({ x: 0, y: 0 });
  const [isDetailDragging, setIsDetailDragging] = useState(false);
  const [detailDragStart, setDetailDragStart] = useState({ x: 0, y: 0 });

  const handleDetailMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    setIsDetailDragging(true);
    setDetailDragStart({
      x: e.clientX - detailModalPos.x,
      y: e.clientY - detailModalPos.y,
    });
  };

  useEffect(() => {
    const handleDetailMouseMove = (e) => {
      if (!isDetailDragging) return;
      setDetailModalPos({
        x: e.clientX - detailDragStart.x,
        y: e.clientY - detailDragStart.y,
      });
    };

    const handleDetailMouseUp = () => {
      setIsDetailDragging(false);
    };

    if (isDetailDragging) {
      window.addEventListener("mousemove", handleDetailMouseMove);
      window.addEventListener("mouseup", handleDetailMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleDetailMouseMove);
      window.removeEventListener("mouseup", handleDetailMouseUp);
    };
  }, [isDetailDragging, detailDragStart]);

  useEffect(() => {
    if (viewingStudent && !isMobile) {
      setDetailModalPos({ x: 0, y: 0 });
    }
  }, [viewingStudent, isMobile]);

  return {
    onTouchStart,
    onTouchMove,
    onListSwipeEnd,
    onModalSwipeEnd,
    onVerticalSwipeEnd,
    creationModalPos,
    handleMouseDown,
    logDetailModalPos,
    handleLogDetailMouseDown,
    detailModalPos,
    handleDetailMouseDown,
  };
}
